import React, {FunctionComponent, useState, useEffect, useRef} from "react"
// icons
import {IconContext} from "react-icons/lib"
import {IoChevronDown} from "react-icons/io5"
import {IoMdMore} from "react-icons/io"
import {
	RequirementBlockActionPointDTO,
	UpdateBlockActionPointRequest,
	UpdateBlockActionPointResponseRequest,
	DeleteBlockActionPointRequest
} from "../api/models"
import {
	useUpdateCustomerBlockActionPoint,
	useUpdateRequirementBlockActionPointResponse,
	useDeleteCustomerBlockActionPoint
} from "../api/mutations"
import {actionOptions} from "@murphy-frontend/web-core/constants"
import {translateConstants} from "../../../common/services/translationService"
import {useUserPreferences} from "../../../common/contexts/UserPreferencesContext"
import {useCustomer} from "../../../common/contexts/CustomerContext"
import EditRow from "./EditRow"
import ModifyOptionsRow from "./ModifyOptions"
import {useParams} from "react-router-dom"

interface ActionsRowProps {
	action: RequirementBlockActionPointDTO
}

const ActionRow: FunctionComponent<ActionsRowProps> = ({action}) => {
	const [description, setDescription] = useState(action.Description)
	const [enableEdit, setEnableEdit] = useState(false)
	const [showDropdown, setShowDropDown] = useState(false)

	const myRef = useRef<HTMLDivElement>(null)

	const {customer} = useCustomer()
	const {translations} = useUserPreferences()
	let {customerId} = useParams()

	const {mutate: updateCustomerActionPoint, isPending: updateCustomerActionPointIsLoading} =
		useUpdateCustomerBlockActionPoint()
	const {mutate: deleteCustomerActionPoint, isPending: deleteCustomerActionPointIsLoading} =
		useDeleteCustomerBlockActionPoint()
	const {mutate: updateActionPointResponse, isPending: updateActionPointResponseIsLoading} =
		useUpdateRequirementBlockActionPointResponse()

	useEffect(() => {
		setDescription(action.Description)
	}, [action.Description])

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		e.preventDefault()
		setDescription(e.target.value)
	}

	const onCancel = () => {
		setDescription(action.Description)
		setEnableEdit(false)
	}

	const handleClickInside = () => setShowDropDown(true)

	function handleClickOutside(e: MouseEvent) {
		if (!myRef.current.contains(e.target as HTMLInputElement)) {
			setShowDropDown(false)
		}
	}

	useEffect(() => {
		document.addEventListener("click", handleClickOutside)
		return () => document.removeEventListener("click", handleClickOutside)
	}, [])

	const onSave = () => {
		const submitUpdateBlock = () => {
			let cId = customerId
			if (!cId) cId = customer.Id
			const request: UpdateBlockActionPointRequest = {
				CustomerId: cId,
				Description: description,
				RequirementBlockActionPointId: action.RequirementBlockActionPointId
			}
			updateCustomerActionPoint(request, {
				onSettled: () => {
					console.log("done")
					setEnableEdit(false)
				},
				onError: () => {
					setEnableEdit(false)
				}
			})
		}
		submitUpdateBlock()
	}

	const onDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const submitUpdateActionResponse = () => {
			let cId = customerId
			if (!cId) cId = customer.Id
			const request: UpdateBlockActionPointResponseRequest = {
				CustomerId: cId,
				Status: e.target.value,
				RequirementBlockActionPointId: action.RequirementBlockActionPointId
			}
			updateActionPointResponse(request, {
				onSettled: () => {
					console.log("done")
				},
				onError: () => {}
			})
		}

		submitUpdateActionResponse()
	}

	const onEdit = () => {
		setShowDropDown(false)
		setEnableEdit(true)
	}

	const onDelete = () => {
		setShowDropDown(false)
		const submitDeleteBlock = () => {
			let cId = customerId
			if (!cId) cId = customer.Id
			const request: DeleteBlockActionPointRequest = {
				CustomerId: cId,
				RequirementBlockActionPointId: action.RequirementBlockActionPointId
			}
			deleteCustomerActionPoint(request, {
				onSettled: () => {
					console.log("done")
				},
				onError: () => {}
			})
		}
		submitDeleteBlock()
	}

	return (
		<div className='actions-row'>
			{!enableEdit && <div className='actions-text'>{action.Description}</div>}
			<EditRow
				description={description}
				enableEdit={enableEdit}
				onSave={onSave}
				onCancel={onCancel}
				handleTextChange={handleDescriptionChange}
				isLoading={updateCustomerActionPointIsLoading}
			/>
			<div className='actions-select-container'>
				<div className='actions-select'>
					<IconContext.Provider value={{className: "btn-icon"}}>
						<IoChevronDown />
					</IconContext.Provider>
					<select className='actions-dropdown' defaultValue={action.Status} onChange={onDropDownChange}>
						{actionOptions.map(opt => (
							<option key={opt.id} value={opt.value}>
								{translations[opt.translationKey]}
							</option>
						))}
					</select>
				</div>
				<div className='actions-more-wrapper' onClick={handleClickInside} ref={myRef}>
					<IconContext.Provider value={{className: "btn-icon actions-more"}}>
						<IoMdMore />
					</IconContext.Provider>
				</div>
			</div>
			<ModifyOptionsRow onEdit={onEdit} onDelete={onDelete} showDropDown={showDropdown} />
		</div>
	)
}

export default ActionRow
